import { z } from 'zod'

/**
 * Preflight result — Part Three D12, register §6.
 *
 * Blocking errors prevent activation; suggestions are dismissible. The result also
 * carries the review revision it was computed against, so entitlement changes force a
 * fresh review and no post-activation rewrite is ever presented as a preflight result.
 */

export const PREFLIGHT_RESULT_VERSION = 1 as const

export const PreflightChecklistItem = z.enum([
  'SOURCE_READINESS',
  'PARTICIPANT_ROUTING_VALIDITY',
  'REQUIRED_ADOPTION_FIELDS',
  'OWNER_GEOMETRY_VALIDITY',
  'LOCKED_VALUES',
  'FORMULA_STRUCTURE',
  'ATTACHMENT_ENTITLEMENT_AND_LIMITS',
  'SUBJECT_MESSAGE_DEADLINE_REMINDER_VALIDITY',
  'ACTIVE_REQUEST_ALLOWANCE',
  'MANAGED_DELIVERY_ENTITLEMENT',
  'REVIEW_REVISION',
])
export type PreflightChecklistItem = z.infer<typeof PreflightChecklistItem>

export const PreflightSeverity = z.enum(['BLOCKING', 'SUGGESTION'])
export type PreflightSeverity = z.infer<typeof PreflightSeverity>

export const PreflightFinding = z
  .object({
    item: PreflightChecklistItem,
    severity: PreflightSeverity,
    code: z.string().min(1).max(64),
    message_key: z.string().min(1).max(160),
    /** An entitlement explanation carries a corrective action, not an upsell loop. */
    recovery_action: z.string().min(1).max(160).optional(),
    field_id: z.string().min(1).max(64).optional(),
  })
  .strict()
export type PreflightFinding = z.infer<typeof PreflightFinding>

export const PreflightResult = z
  .object({
    result_version: z.literal(PREFLIGHT_RESULT_VERSION),
    /** Revision the review was computed against; a stale result is recomputed. */
    review_revision: z.number().int().positive(),
    findings: z.array(PreflightFinding),
    blocking_count: z.number().int().nonnegative(),
    suggestion_count: z.number().int().nonnegative(),
    ready: z.boolean(),
  })
  .strict()
  .superRefine((result, ctx) => {
    const blocking = result.findings.filter((finding) => finding.severity === 'BLOCKING').length
    const suggestions = result.findings.length - blocking
    if (result.blocking_count !== blocking) {
      ctx.addIssue({
        code: 'custom',
        message: 'blocking_count must match the blocking findings',
        path: ['blocking_count'],
      })
    }
    if (result.suggestion_count !== suggestions) {
      ctx.addIssue({
        code: 'custom',
        message: 'suggestion_count must match the non-blocking findings',
        path: ['suggestion_count'],
      })
    }
    if (result.ready !== (blocking === 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'activation readiness means zero blocking findings',
        path: ['ready'],
      })
    }
  })
export type PreflightResult = z.infer<typeof PreflightResult>
